"use client";

// The public producer details on a profile page (free producer account, beta). The api sends
// these on the person read only under the label rule (switch on, an approved profile that is
// not held or suspended, and not turned off by staff); otherwise they are null and this renders
// nothing.
//
// - Business name and what they produce: shown with the same contact scrub the profile's bio
//   gets in the ui (scrubContactInfo, ProfileSidebar). The api also scrubs both for guests.
// - Website: a plain link, rel "ugc nofollow noopener" (member-written), opening in a new tab like the profile's
//   other links (ProfileMoreInfoAccordion). The link text is the address's host. The bio rules
//   hide no links, so guests see it too.
// - State: the state name, beside a map pin, as the header shows the profile's location.

import React from "react";
import { MapPin } from "lucide-react";
import ProducerBadge from "@/components/common/ProducerBadge";
import { scrubContactInfo } from "@/lib/scrubContact";
import { isSafeWebsite, websiteLinkText, type PublicProducerDetails } from "@/lib/producerLabel";
import { US_STATE_OPTIONS } from "@/lib/producerApi";

export const PRODUCER_DETAILS_TEXT = {
  producesHeading: "What they produce",
} as const;

function stateName(code: string | null): string | null {
  if (!code) return null;
  const up = code.trim().toUpperCase();
  return US_STATE_OPTIONS.find((s) => s.code === up)?.name ?? null;
}

export default function ProducerDetailsCard({ details }: { details: PublicProducerDetails | null }) {
  if (!details) return null;
  const website = isSafeWebsite(details.website) ? details.website : null;
  const state = stateName(details.state);
  return (
    <div className="rounded-2xl border border-border bg-card text-card-foreground shadow-sm p-5 space-y-3" data-testid="producer-details">
      <ProducerBadge role={details.role} tone="light" />
      {details.businessName && (
        <p className="font-serif text-lg font-semibold leading-snug break-words text-zinc-900 dark:text-zinc-100" data-testid="producer-business-name">
          {scrubContactInfo(details.businessName)}
        </p>
      )}
      {website && (
        <p className="text-sm">
          <a
            href={website}
            target="_blank"
            rel="ugc nofollow noopener"
            className="underline break-all"
            data-testid="producer-website"
          >
            {websiteLinkText(website)}
          </a>
        </p>
      )}
      {state && (
        <p className="inline-flex items-center gap-1 text-sm text-zinc-600 dark:text-zinc-300" data-testid="producer-state">
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {state}
        </p>
      )}
      {details.produces && (
        <div>
          <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {PRODUCER_DETAILS_TEXT.producesHeading}
          </h3>
          <p className="text-sm whitespace-pre-line break-words text-zinc-800 dark:text-zinc-200" data-testid="producer-produces">
            {scrubContactInfo(details.produces)}
          </p>
        </div>
      )}
    </div>
  );
}
